import { useState, useEffect, useMemo, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { computeValidCodes } from '../utils/approvalStateUtils';

/**
 * Centralised approval-tracking hook for all review document (RD_*.js) pages.
 *
 * Computes totalParagraphs from the current docStructure + reportDataOutputs,
 * loads approvedParagraphs from Firestore, and intersects them with the set of
 * currently valid paragraph codes. This intersection is the key improvement over
 * the previous per-page logic: orphaned approvedParagraphs keys (from paragraphs
 * that no longer exist or were blanked during backend cleanup) are excluded from
 * reviewedCodes, preventing them from inflating reviewedCodes.size and blocking
 * the isFullyApproved check.
 *
 * @param {Object}  params
 * @param {string}  params.patientId          - Patient document ID
 * @param {number}  params.moduleNumber        - manageReports section key (e.g. 1, 3)
 * @param {Object}  params.docStructure        - Section -> paragraph map with { code } entries
 * @param {Object}  params.reportDataOutputs   - Outputs from manageReports (code -> content)
 * @param {Set<string>} [params.excludedCodes]  - Optional set of codes to exclude from validity
 *                                                 (e.g. strength paragraphs the clinician has deleted
 *                                                 this session). Defaults to an empty Set.
 *
 * @returns {{
 *   totalParagraphs: number,
 *   reviewedCodes: Set<string>,
 *   isFullyApproved: boolean,
 *   showWarning: boolean,
 *   addReviewedCode: (code: string) => void,
 *   removeReviewedCode: (code: string) => void,
 * }}
 */
const EMPTY_SET = new Set();

export function useApprovalTracking({
  patientId,
  moduleNumber,
  docStructure,
  reportDataOutputs,
  excludedCodes = EMPTY_SET,
}) {
  const [reviewedCodes, setReviewedCodes] = useState(new Set());
  const [totalParagraphs, setTotalParagraphs] = useState(0);

  // Derive the set of paragraph codes that currently have content and are
  // not explicitly excluded (e.g. strength paragraphs deleted this session).
  // Only these codes are considered "valid" for approval tracking —
  // any approvedParagraphs keys NOT in this set are stale leftovers.
  //
  // The derivation is delegated to the shared computeValidCodes helper so that
  // this hook (which drives the on-screen progress) and the exit-transaction
  // write (which persists xReportApproved) use identical logic and can never
  // disagree about which paragraphs count.
  const validCodes = useMemo(
    () => computeValidCodes(docStructure, reportDataOutputs, excludedCodes),
    [docStructure, reportDataOutputs, excludedCodes]
  );

  // totalParagraphs = count of valid codes with content
  useEffect(() => {
    setTotalParagraphs(validCodes.size);
  }, [validCodes]);

  // Load approvedParagraphs from Firestore, intersected with validCodes.
  // The intersection ensures orphaned approval keys (from deleted or blanked
  // paragraphs) are excluded, fixing the isFullyApproved mismatch.
  useEffect(() => {
    let isMounted = true;

    const loadApprovedParagraphs = async () => {
      try {
        const docRef = doc(db, 'patients', patientId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists() || !isMounted) return;

        const data = docSnap.data();
        const approved =
          data.dataModel?.['6_manageReports']?.sections?.[`${moduleNumber}`]
            ?.approvedParagraphs || {};

        // Only count approvals for codes that are currently valid
        const filtered = new Set(
          Object.keys(approved).filter(code => validCodes.has(code))
        );

        if (isMounted) {
          setReviewedCodes(filtered);
        }
      } catch (error) {
        console.error('Error loading approved paragraphs:', error);
      }
    };

    if (validCodes.size > 0) {
      loadApprovedParagraphs();
    }

    return () => {
      isMounted = false;
    };
  }, [patientId, moduleNumber, validCodes]);

  const isFullyApproved =
    totalParagraphs > 0 && totalParagraphs === reviewedCodes.size;

  const showWarning = totalParagraphs > reviewedCodes.size;

  // Optimistic-update callbacks for individual paragraph approval actions.
  // These mirror the set manipulation previously inlined in each RD_*.js file.
  const addReviewedCode = useCallback(code => {
    setReviewedCodes(prev => new Set([...prev, code]));
  }, []);

  const removeReviewedCode = useCallback(code => {
    setReviewedCodes(prev => {
      const next = new Set(prev);
      next.delete(code);
      return next;
    });
  }, []);

  return {
    totalParagraphs,
    reviewedCodes,
    isFullyApproved,
    showWarning,
    addReviewedCode,
    removeReviewedCode,
  };
}
